// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Lead Quality Chart
// Qualification score distribution
// ═══════════════════════════════════════════════════════════════════════════════

import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface QualityItem {
  range: string;
  count: number;
  percentage: number;
}

interface LeadQualityChartProps {
  data: QualityItem[];
}

export function LeadQualityChart({ data }: LeadQualityChartProps) {
  if (data.length === 0 || data.every(d => d.count === 0)) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg">Lead Quality</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No leads captured yet
          </p>
        </CardContent>
      </Card>
    );
  }

  const maxCount = Math.max(...data.map(d => d.count), 1);

  const rangeTones: Record<string, 'success' | 'info' | 'warning' | 'neutral'> = {
    '80-100': 'success',
    '60-79': 'info',
    '40-59': 'warning',
    '0-39': 'neutral',
  };

  const rangeClasses: Record<
    'success' | 'info' | 'warning' | 'neutral',
    { text: string; bar: string; legend: string }
  > = {
    success: {
      text: 'text-status-success',
      bar: 'bg-status-success/20 border-l-status-success',
      legend: 'bg-status-success',
    },
    info: {
      text: 'text-status-info',
      bar: 'bg-status-info/20 border-l-status-info',
      legend: 'bg-status-info',
    },
    warning: {
      text: 'text-status-warning',
      bar: 'bg-status-warning/20 border-l-status-warning',
      legend: 'bg-status-warning',
    },
    neutral: {
      text: 'text-foreground',
      bar: 'bg-surface border-l-border-strong',
      legend: 'bg-text-tertiary',
    },
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">Lead Quality Distribution</CardTitle>
        <p className="text-sm text-muted-foreground">
          Qualification score ranges
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {data.map((item, index) => {
            const width = (item.count / maxCount) * 100;
            const tone = rangeTones[item.range] || 'neutral';
            const toneClasses = rangeClasses[tone];

            return (
              <motion.div
                key={item.range}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-foreground">
                    Score {item.range}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={cn('text-sm font-bold', toneClasses.text)}>
                      {item.count}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({item.percentage}%)
                    </span>
                  </div>
                </div>

                <div className="relative h-8 rounded-lg overflow-hidden bg-surface">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${width}%` }}
                    transition={{ 
                      duration: 0.8, 
                      delay: index * 0.1,
                      ease: "easeOut"
                    }}
                    className={cn(
                      'absolute inset-y-0 left-0 rounded-lg border-l-4',
                      toneClasses.bar
                    )}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-6 pt-4 border-t border-border">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <div className={cn('h-3 w-3 rounded-sm', rangeClasses.success.legend)} />
              <span className="text-muted-foreground">80-100: Excellent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn('h-3 w-3 rounded-sm', rangeClasses.info.legend)} />
              <span className="text-muted-foreground">60-79: Good</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn('h-3 w-3 rounded-sm', rangeClasses.warning.legend)} />
              <span className="text-muted-foreground">40-59: Medium</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn('h-3 w-3 rounded-sm', rangeClasses.neutral.legend)} />
              <span className="text-muted-foreground">0-39: Low</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
